import { useEffect, useState } from "react";
import { api, gbp, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Banknote, FileDown, FileSpreadsheet, Loader2, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function fmtMonth(m) {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}
function fmtDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5" data-testid={testid}>
      <p className="text-sm text-slate-500">{label}</p>
      <p className={`mt-2 text-2xl font-bold font-admin-head ${accent || "text-slate-900 dark:text-white"}`}>{value}</p>
    </div>
  );
}

export default function Reconciliation() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (m) => {
    setLoading(true);
    api.get(`/reports/reconciliation${m ? `?month=${m}` : ""}`)
      .then((r) => { setData(r.data); setMonth(r.data.month); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const doExport = async (fmt) => {
    try {
      await downloadFile(`/reports/reconciliation/export?fmt=${fmt}&month=${month}`, `reconciliation_${month}.${fmt}`);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed"); }
  };

  const t = data?.totals;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="reconciliation-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-2xl grid place-items-center text-white" style={{ background: "var(--brand-primary, #4f46e5)" }}><Banknote className="h-5 w-5" /></div>
          <div>
            <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Payout Reconciliation</h1>
            <p className="text-slate-500 mt-0.5">Match each monthly Stripe deposit to its transactions and fees.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={month || undefined} onValueChange={(v) => load(v)}>
            <SelectTrigger data-testid="recon-month-select" className="w-48"><SelectValue placeholder="Select month" /></SelectTrigger>
            <SelectContent>
              {(data?.months || []).map((m) => <SelectItem key={m} value={m}>{fmtMonth(m)}</SelectItem>)}
              {data && !data.months.length && <SelectItem value={data.month}>{fmtMonth(data.month)}</SelectItem>}
            </SelectContent>
          </Select>
          <Button data-testid="recon-export-csv" variant="outline" onClick={() => doExport("csv")} disabled={!month}><FileDown className="h-4 w-4 mr-1.5" /> CSV</Button>
          <Button data-testid="recon-export-xlsx" variant="outline" onClick={() => doExport("xlsx")} disabled={!month}><FileSpreadsheet className="h-4 w-4 mr-1.5" /> XLSX</Button>
        </div>
      </div>

      {loading || !data ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="rounded-xl border border-indigo-200 dark:border-indigo-900 bg-indigo-50/60 dark:bg-indigo-950/30 p-4 flex items-center gap-3" data-testid="recon-payout-banner">
            <CalendarClock className="h-5 w-5 text-indigo-600 shrink-0" />
            <p className="text-sm text-indigo-800 dark:text-indigo-200">
              Expected Stripe deposit for <b>{fmtMonth(data.month)}</b> is <b>{gbp(t.net_payout)}</b>, paid out on <b>{fmtDate(data.payout_date)}</b>. This is gross takings minus Stripe fees ({(data.fee_percent * 100).toFixed(1)}% + {gbp(data.fee_fixed)}/txn) and any refunds.
            </p>
          </div>

          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            <StatCard label="Gross takings" value={gbp(t.gross)} testid="recon-stat-gross" />
            <StatCard label="Stripe fees" value={`− ${gbp(t.fee)}`} accent="text-red-500" testid="recon-stat-fee" />
            <StatCard label="Refunds" value={`− ${gbp(t.refund)}`} accent="text-amber-600" testid="recon-stat-refund" />
            <StatCard label="Net payout (deposit)" value={gbp(t.net_payout)} accent="text-emerald-600" testid="recon-stat-net" />
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Transactions in {fmtMonth(data.month)} ({t.count})</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="recon-table">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-100 dark:border-slate-800">
                    <th className="py-2 font-medium">Date</th>
                    <th className="py-2 font-medium">Transaction</th>
                    <th className="py-2 font-medium">Kiosk</th>
                    <th className="py-2 font-medium text-right">Gross</th>
                    <th className="py-2 font-medium text-right">Stripe fee</th>
                    <th className="py-2 font-medium text-right">Refund</th>
                    <th className="py-2 font-medium text-right">Net payout</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.id} data-testid={`recon-row-${r.transaction_id}`} className="border-b border-slate-50 dark:border-slate-800/50">
                      <td className="py-2 text-xs text-slate-500">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="py-2 text-xs font-mono-x text-slate-500">{r.transaction_id}</td>
                      <td className="py-2 text-slate-700 dark:text-slate-300">{r.kiosk_name}</td>
                      <td className="py-2 text-right">{gbp(r.gross)}</td>
                      <td className="py-2 text-right text-red-500">− {gbp(r.fee)}</td>
                      <td className="py-2 text-right text-amber-600">{r.refund ? `− ${gbp(r.refund)}` : "—"}</td>
                      <td className="py-2 text-right font-semibold text-emerald-600">{gbp(r.net)}</td>
                    </tr>
                  ))}
                  {!data.rows.length && <tr><td colSpan={7} className="py-6 text-center text-slate-400">No settled transactions in this month.</td></tr>}
                </tbody>
                {data.rows.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-200 dark:border-slate-700 font-semibold">
                      <td className="py-2" colSpan={3}>Total · {t.count} transactions</td>
                      <td className="py-2 text-right">{gbp(t.gross)}</td>
                      <td className="py-2 text-right text-red-500">− {gbp(t.fee)}</td>
                      <td className="py-2 text-right text-amber-600">{t.refund ? `− ${gbp(t.refund)}` : "—"}</td>
                      <td className="py-2 text-right text-emerald-600">{gbp(t.net_payout)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
